import React, { useEffect, useMemo, useRef } from 'react'
import { Platform, Text } from 'react-native'
import { useTheme } from 'react-native-paper'
import { useIsFocused } from '@react-navigation/native'
import PropTypes from 'prop-types'
import { helpers } from '@linqpal/common-frontend'
import { webSpecificStyle } from '@linqpal/common-frontend/src/helpers'
import {
  useFocusedErrorProps,
  useThemeStyle,
} from '@linqpal/common-frontend/src/hooks'
import { InputText } from '@linqpal/common-frontend/src/ui'
import {
  InputFieldContainer,
  InputFieldOuterContainer,
} from './InputFieldContainer'

import { useBaseInputStyle } from '@linqpal/common-frontend/src/ions/style'
import { KeyboardType } from '@linqpal/models/src/dictionaries'

const { composeStyle } = helpers

const useCommonTextInputStyles = () =>
  useThemeStyle((theme) => ({
    countryCodeInput: {
      maxWidth: 36,
      marginRight: 8,
    },
    inputOuterStyle: {
      borderRadius: 8,
      height: 30,
    },
    input: {
      backgroundColor: 'transparent',
      color: '#003353',
      flex: 1,
      fontSize: 18,
      fontWeight: '400',
      minWidth: 200,
      marginTop: 0,
      width: '100%',
    },
  }))

const propValuesMap = {
  autoCompleteType: {
    off: 'off',
    username: 'username',
    password: 'password',
    'new-password': 'password',
    'current-password': 'password',
    email: 'email',
    name: 'name',
    nickname: 'name',
    tel: 'tel',
    'tel-national': 'tel',
    'tel-local': 'tel',
    'street-address': 'street-address',
    'postal-code': 'postal-code',
    'cc-number': 'cc-number',
    'cc-csc': 'cc-csc',
    'cc-exp': 'cc-exp',
    'cc-exp-month': 'cc-exp-month',
    'cc-exp-year': 'cc-exp-year',
  },
  textContentType: {
    off: 'none',
    username: 'username',
    password: 'password',
    tel: 'telephoneNumber',
    url: 'URL',
    'address-level2': 'addressCity',
    'address-level1': 'addressCityAndState',
    addressState: 'addressState',
    'country-name': 'countryName',
    'cc-number': 'creditCardNumber',
    email: 'emailAddress',
    'family-name': 'familyName',
    'street-address': 'fullStreetAddress',
    'given-name': 'givenName',
    'additional-name': 'middleName',
    name: 'name',
    'honorific-prefix': 'namePrefix',
    'honorific-suffix': 'nameSuffix',
    nickname: 'nickname',
    'organization-title': 'organizationName',
    'postal-code': 'postalCode',
    'address-line1': 'streetAddressLine1',
    'address-line2': 'streetAddressLine2',
    'address-level4': 'sublocality',
  },
}

function useAutoComplete(props) {
  const { autoComplete, ...rest } = props
  const newProps = useMemo(() => {
    if (typeof autoComplete !== 'string') return { autoComplete }

    switch (Platform.OS) {
      case 'android':
      case 'ios':
        const osSpecificProperty =
            Platform.OS === 'android' ? 'autoCompleteType' : 'textContentType',
          newValue = propValuesMap[osSpecificProperty][autoComplete]
        if (typeof newValue === 'string') {
          return { [osSpecificProperty]: newValue }
        }
      default:
        return { autoComplete }
    }
  }, [autoComplete])
  return { ...newProps, ...rest }
}

const Input = (props) => {
  const { onBlur, onFocus, focused } = useFocusedErrorProps()
  const { onBlur: onInputBlur, onFocus: onInputFocus, disabled } = props

  const theme = useTheme()
  const {
    colors: { accentText },
  } = theme
  const { inputBackgroundColor } = useBaseInputStyle()

  const {
      inputRef,
      style,
      autoFocus,
      inputOuterStyle,
      innerBorderStyle,
      ...rest
    } = useAutoComplete(props),
    styles = useCommonTextInputStyles()

  const { highlight, error: initialError, label, mandatory } = rest
  const [error, setError] = React.useState(initialError)
  if (inputRef) rest.ref = inputRef
  const { primary } = useTheme().colors
  if (!rest.placeholderTextColor) {
    rest.placeholderTextColor = '#99ADBA'
  }
  const inputStyle = useMemo(() => {
    return composeStyle(
      styles.input,
      webSpecificStyle({
        caretColor: primary,
        outline: 'none',
      }),
      style,
    )
  }, [style])

  const isFocused = useIsFocused()
  const ref = useRef()

  useEffect(() => {
    if (ref.current && autoFocus && isFocused) {
      ref.current.focus()
    }
  }, [isFocused])

  const labelText = (
    <Text
      style={composeStyle({ fontSize: 16 }, disabled && { color: '#99ADBA' })}
    >
      {label}
      {mandatory ? <Text style={{ paddingLeft: 2 }}>*</Text> : null}
    </Text>
  )

  return (
    <InputFieldOuterContainer
      focused={focused}
      style={[
        style,
        disabled && { borderColor: '#F5F7F8' },
        highlight ? { borderColor: '#DD4A2C' } : {},
      ]}
      error={error}
    >
      <InputText
        {...rest}
        ref={ref}
        style={inputStyle}
        label={labelText}
        labelActiveColor={accentText}
        dense
        theme={{ roundness: 8 }}
        inputOuterStyle={composeStyle(
          inputBackgroundColor,
          {
            flex: 1,
            borderRadius: 8,
          },
          disabled && { backgroundColor: '#F5F7F8' },
        )}
        onFocus={onInputFocus}
        onBlur={onInputBlur}
        InputContainer={InputFieldContainer}
        underlineColor="transparent"
      />
    </InputFieldOuterContainer>
  )
}

const CommonInput = ({
  value = '',
  onChangeText,
  autoFocus = false,
  autoComplete = 'off',
  editable = true,
  keyboardType = KeyboardType.Default,
  returnKeyType = 'go',
  onSubmitEditing,
  placeholder = '',
  placeholderTextColor = '',
  secureTextEntry = false,
  style = {},
  ...props
}) => (
  <Input
    value={value}
    onChangeText={onChangeText}
    autoFocus={autoFocus}
    autoComplete={autoComplete}
    editable={editable}
    keyboardType={keyboardType}
    returnKeyType={returnKeyType}
    onSubmitEditing={onSubmitEditing}
    placeholder={placeholder}
    placeholderTextColor={placeholderTextColor}
    secureTextEntry={secureTextEntry}
    style={style}
    {...props}
  />
)

export default CommonInput
